/**
 * #88 — manually add a control exception the AI missed. Opens its A13
 * server-side; management then concludes on it like an AI-extracted one.
 */
import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormField,
  Input,
  Modal,
  SpaceBetween,
  Textarea,
} from "@cloudscape-design/components";
import { createException } from "./api";

export function AddExceptionModal({
  reportId,
  visible,
  onDismiss,
  onAdded,
}: {
  reportId: string;
  visible: boolean;
  onDismiss: () => void;
  onAdded: () => void;
}) {
  const [controlId, setControlId] = useState("");
  const [objective, setObjective] = useState("");
  const [description, setDescription] = useState(""); // auditor's exception
  const [mgmtResponse, setMgmtResponse] = useState("");
  const [page, setPage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setControlId("");
    setObjective("");
    setDescription("");
    setMgmtResponse("");
    setPage("");
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const pageNum = Number.parseInt(page, 10);
      await createException(reportId, {
        control_id: controlId,
        description, // auditor's exception
        control_objective: objective.trim() || null,
        management_response: mgmtResponse.trim() || null,
        page: Number.isFinite(pageNum) && pageNum > 0 ? pageNum : null,
      });
      reset();
      onAdded();
      onDismiss();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Add control exception"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onDismiss} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={save}
              disabled={busy || !controlId.trim() || !objective.trim() || !description.trim()}
              loading={busy}
            >
              Add exception
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="s">
        {error && (
          <Alert type="error" header="Add failed">
            {error}
          </Alert>
        )}
        <FormField label="Control ID">
          <Input
            value={controlId}
            onChange={({ detail }) => setControlId(detail.value)}
            placeholder="e.g. CC6.1"
          />
        </FormField>
        <FormField label="Control objective" description="What the control is meant to achieve.">
          <Textarea
            value={objective}
            onChange={({ detail }) => setObjective(detail.value)}
            rows={3}
            placeholder="The control objective this exception relates to."
          />
        </FormField>
        <FormField label="Auditor's exception" description="What the auditor found / the control that failed.">
          <Textarea
            value={description}
            onChange={({ detail }) => setDescription(detail.value)}
            rows={4}
            placeholder="The deviation the service auditor noted."
          />
        </FormField>
        <FormField
          label="Service organization's management response (optional)"
          description="How the service org responded to the exception, per the report."
        >
          <Textarea
            value={mgmtResponse}
            onChange={({ detail }) => setMgmtResponse(detail.value)}
            rows={3}
            placeholder="The service org's stated remediation / response."
          />
        </FormField>
        <FormField label="Page in the report (optional)" description="Adds a 'View p.N' link on the card.">
          <Input
            value={page}
            onChange={({ detail }) => setPage(detail.value.replace(/[^0-9]/g, ""))}
            type="number"
            inputMode="numeric"
            placeholder="e.g. 42"
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}
